import axios from "axios"

import type {
  FairUseEntitlementsFormState,
  FairUseVoicePriceRow,
} from "./fair-use-entitlements-utils"
import type { FairUseImagePriceRow, FairUseVoiceTokenPriceRow } from "./fair-use-pricing-utils"
import type {
  FairUseConfig,
  FairUseConfigResponse,
  FairUseConfigUpdate,
  FairUseDowngradeModelOption,
  FairUseEntitlements,
  FairUseErrorCode,
  FairUseImagePrices,
  FairUseOverride,
  FairUseOverviewRow,
  FairUsePersonIdentity,
  FairUsePlan,
  FairUseReasoningEffort,
  FairUseStatus,
  FairUseTier,
  FairUseVoicePrices,
  FairUseVoiceTokenPrices,
} from "@/api/admin/fair-use"
import type { UsageColorBand } from "@/features/fair-use/fair-use-utils"

import { FairUseApiError } from "@/api/admin/fair-use"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import {
  getUsageColorBand,
  truncateOid,
  usageProgressIndicatorClass,
} from "@/features/fair-use/fair-use-utils"

import {
  fairUseEntitlementsApplied,
  fairUseEntitlementsToFormState,
  fairUseVoicePricesApplied,
  fairUseVoicePricesToFormState,
} from "./fair-use-entitlements-utils"
import {
  fairUseImagePricesApplied,
  fairUseImagePricesToFormState,
  fairUseVoiceTokenPricesApplied,
  fairUseVoiceTokenPricesToFormState,
} from "./fair-use-pricing-utils"

// Non-component helpers for the fair-use admin surface. Kept out of the .tsx
// component files so they export components only (react-refresh rule).

/**
 * What to call this person: their name, else their e-mail, else a shortened
 * oid. Takes the identity half only, so the people table (whose rows may carry
 * no usage) and the operations table read the same name.
 */
export function fairUseDisplayName(row: FairUsePersonIdentity): string {
  if (row.profile?.name) return row.profile.name
  if (row.profile?.email) return row.profile.email
  if (row.user.length > 16) return truncateOid(row.user)
  return row.user
}

type FairUseBadgeProps = { variant: "outline" | "destructive"; className: string }

/**
 * Badge appearance per usage band — a graduated scale matching the docint
 * warning-badge conventions: plain -> yellow outline -> soft red ->
 * solid red. The hue per band is the bar's own palette
 * ({@link usageProgressIndicatorClass}); only the SHAPE differs, because a
 * badge is a chip and not a fill.
 */
const BAND_BADGE_PROPS: Record<UsageColorBand, FairUseBadgeProps> = {
  green: { variant: "outline", className: "" },
  yellow: {
    variant: "outline",
    className: STATUS_TONES.warning.soft,
  },
  orange: {
    variant: "outline",
    className: STATUS_TONES.destructive.soft,
  },
  red: { variant: "destructive", className: STATUS_TONES.destructive.solid },
}

/**
 * Badge styling for an overview row.
 *
 * Driven by the SAME band selection as the row's progress bar
 * ({@link fairUseProgressIndicatorClass}) on purpose: the two render inches
 * apart in one table row, so a badge with its own status -> colour map made
 * the row contradict itself — a user at 80 % with status `warn` got an orange
 * bar next to a yellow badge, and one at 105 % a red bar next to an amber one.
 */
export function fairUseStatusBadgeProps(status: FairUseStatus, percent: number): FairUseBadgeProps {
  return BAND_BADGE_PROPS[getUsageColorBand(percent, status)]
}

/**
 * Progress-bar fill class for an overview row; pass to the
 * `indicatorClassName` prop of <Progress>.
 *
 * Band selection AND palette both come from the user surface
 * ({@link getUsageColorBand}) on purpose: this file used to keep its own
 * status -> band map, so a user at 80 % with status `warn` rendered orange in
 * the composer indicator and yellow in this table at the very same instant.
 */
export function fairUseProgressIndicatorClass(status: FairUseStatus, percent: number): string {
  return usageProgressIndicatorClass(getUsageColorBand(percent, status))
}

/**
 * Concatenated overview pages in one monotonic cost order.
 *
 * The backend sorts each page by cost descending BUT appends the users who
 * have a grant or a pending request and no rollup this month (cost 0.00) to
 * the end of page 1, after the sorted rows. Concatenation therefore does NOT
 * preserve a global order: with >50 users page 1 ends at 0.00 SEK and page 2
 * resumes at e.g. 40 SEK. Re-sorting the whole concatenation is the only way
 * the table reads as cost-ranked. The tiebreak on the user id keeps rows in a
 * deterministic order across renders (and across the equal-cost 0.00 block).
 */
export function sortFairUseRowsByCost(rows: FairUseOverviewRow[]): FairUseOverviewRow[] {
  return [...rows].sort((a, b) => {
    const byCost = b.cost_sek - a.cost_sek
    // NaN (a malformed cost) is falsy here, so it falls through to the id
    // tiebreak rather than producing an inconsistent comparator.
    if (byCost) return byCost
    if (a.user === b.user) return 0
    return a.user < b.user ? -1 : 1
  })
}

/**
 * The current UTC month plus the preceding months as "YYYY-MM" keys, newest
 * first. UTC on purpose — the backend month key convention is UTC (spec §2).
 */
export function getRecentMonthOptions(count = 6): string[] {
  const now = new Date()
  const options: string[] = []
  for (let i = 0; i < count; i++) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1))
    options.push(`${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`)
  }
  return options
}

// =============================================================================
// Error copy — code -> localized explanation (see routes/admin/fair_use.py)
// =============================================================================

/**
 * The server's stable error code for a rejected call, when it sent one.
 *
 * Every admin fair-use call goes through `fairUseRequest` (api/admin/fair-use.ts),
 * whose `toFairUseApiError` turns any response carrying a `code` into a
 * {@link FairUseApiError} — so reading it off the raw axios body here would be
 * unreachable code.
 */
export function fairUseErrorCode(error: unknown): string | undefined {
  return error instanceof FairUseApiError ? error.code : undefined
}

/**
 * The HTTP status of a rejected call, when one is recoverable from the error.
 *
 * The axios branch IS reachable, unlike the one {@link fairUseErrorCode} used
 * to carry: a gateway answering 503 with an HTML body has neither `code` nor
 * `error`, so `toFairUseApiError` hands it back as a raw AxiosError.
 */
export function fairUseErrorStatus(error: unknown): number | undefined {
  if (error instanceof FairUseApiError) return error.status
  if (axios.isAxiosError(error)) return error.response?.status
  return undefined
}

/**
 * The server's own reason for a rejected call, when it sent one.
 *
 * Every route answers with `{"error": "<reason>", "code": "<id>"}` (e.g.
 * "warn_percent must be positive"); showing that beats a generic "Failed to
 * save", which tells the admin nothing about which field to fix. That reason
 * arrives here as the `message` of the {@link FairUseApiError} the api module
 * already built from it. Returns undefined for non-Error rejections.
 */
export function fairUseServerErrorMessage(error: unknown): string | undefined {
  if (error instanceof Error && error.message.trim()) return error.message
  return undefined
}

/**
 * Localized explanation per stable error code.
 *
 * `invalid_request` and `invalid_config` are deliberately absent: their server
 * message names the offending field ("blocks is required and must be a
 * positive integer"), which no generic string can match, so they fall through
 * to the server text in {@link fairUseErrorDescription}. `hash_mismatch` is
 * absent too — the settings section answers it with its own conflict banner
 * before ever asking for a description.
 */
const FAIR_USE_ERROR_COPY: Partial<
  Record<FairUseErrorCode, { key: string; defaultValue: string }>
> = {
  fair_use_not_configured: {
    key: "adminDashboard.fairUse.errors.fair_use_not_configured",
    defaultValue: "Fair use tracking is not configured in this environment.",
  },
  request_not_found: {
    key: "adminDashboard.fairUse.errors.request_not_found",
    defaultValue: "That request no longer exists — it may already have been handled or withdrawn.",
  },
  request_not_pending: {
    key: "adminDashboard.fairUse.errors.request_not_pending",
    defaultValue:
      "That request has already been handled by someone else. Refresh to see its current state.",
  },
  month_locked: {
    key: "adminDashboard.fairUse.errors.month_locked",
    defaultValue: "That billing month is locked, so it cannot be topped up any further.",
  },
  month_in_future: {
    key: "adminDashboard.fairUse.errors.month_in_future",
    defaultValue:
      "That month has not started yet — a top-up only applies to the current or an earlier month.",
  },
  // The Plans & pricing panel already keeps the commercial fields out of a
  // delegated admin's payload, so this 403 should be unreachable — but the
  // server's own text is English-only, and a filter that ever slips would put
  // it in front of a Swedish admin verbatim.
  platform_admin_required: {
    key: "adminDashboard.fairUse.errors.platform_admin_required",
    defaultValue:
      "Plans and prices are set by your platform provider — those fields could not be saved. The limits are yours to change.",
  },
}

/**
 * The explanation to show an admin for a rejected fair-use call: the localized
 * copy for a known `code`, else the server's own message, else undefined so
 * the caller keeps its generic copy ("Failed to approve request") alone.
 *
 * `translate` is the `t` from `useTranslation`, narrowed to the (key,
 * defaultValue) form so this stays a plain testable function.
 */
export function fairUseErrorDescription(
  error: unknown,
  translate: (key: string, defaultValue: string) => string
): string | undefined {
  const code = fairUseErrorCode(error)
  const copy = code ? FAIR_USE_ERROR_COPY[code as FairUseErrorCode] : undefined
  if (copy) return translate(copy.key, copy.defaultValue)
  return fairUseServerErrorMessage(error)
}

/**
 * True when the config-PUT rejection is the optimistic-concurrency 409.
 *
 * The coded error is the whole contract — matching on a `hash_mismatch`
 * message prefix as well would classify ANY Error whose text happens to start
 * with that literal as a config conflict, silently offering a "reload
 * configuration" banner for an unrelated failure.
 */
export function isFairUseConfigConflict(error: unknown): boolean {
  return fairUseErrorCode(error) === "hash_mismatch"
}

/**
 * True when the overview failed because fair use simply isn't provisioned
 * (503 `fair_use_not_configured`) rather than because something broke.
 *
 * Everything else — a 403, a 400 from a stale continuation token, a 500 from a
 * missing Cosmos composite index — must NOT read as "the feature is off".
 */
export function isFairUseNotConfiguredError(error: unknown): boolean {
  return fairUseErrorCode(error) === "fair_use_not_configured" || fairUseErrorStatus(error) === 503
}

/**
 * The reasoning-effort stops the downgrade select offers, weakest first.
 *
 * Mirrors the backend's `VALID_REASONING_EFFORTS`
 * (helloai_utilities/fair_use/constants.py), which is the union ladder across
 * model families — the configured value is narrowed to what the downgrade model
 * of the day actually supports at enforcement time, not here. Offering anything
 * outside this list would only produce a 400 `invalid_config` on save.
 */
export const FAIR_USE_REASONING_EFFORTS: readonly FairUseReasoningEffort[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]

/** The backend's `DEFAULT_DOWNGRADE_REASONING_EFFORT` — cheap but still capable. */
const DEFAULT_REASONING_EFFORT: FairUseReasoningEffort = "low"

/**
 * Coerce a stored effort to one the select can represent.
 *
 * A stored value outside the ladder (an older document, a hand-edited settings
 * blob) would otherwise leave the select with no matching option and render
 * blank — an admin saving from that state would then post whatever the
 * placeholder happened to be. Falling back to the documented default keeps the
 * control honest: what the admin sees is what a save would send.
 */
export function normalizeFairUseReasoningEffort(value: unknown): FairUseReasoningEffort {
  return FAIR_USE_REASONING_EFFORTS.includes(value as FairUseReasoningEffort)
    ? (value as FairUseReasoningEffort)
    : DEFAULT_REASONING_EFFORT
}

/**
 * Editable form mirror of {@link FairUseConfig}. Numeric fields are kept as
 * strings while editing so partially typed values ("7", "7.", "") never
 * collapse to NaN mid-edit; parsing happens once at save time.
 */
export type FairUseConfigFormState = {
  enabled: boolean
  defaultMonthlyCapSek: string
  proMonthlyCapSek: string
  unlimitedMonthlyCapSek: string
  warnPercent: string
  requestCtaPercent: string
  downgradePercent: string
  blockPercent: string
  downgradeModel: string
  downgradeReasoningEffort: FairUseReasoningEffort
  blockCapSek: string
  blockListPriceSek: string
  overrides: Record<string, FairUseOverride>
  /** Round-2 plan entitlements (#3087 R1); empty rows when the backend sent none. */
  entitlements: FairUseEntitlementsFormState
  /** Round-2 voice rates (R5), as editable rows rather than a map. */
  voicePrices: FairUseVoicePriceRow[]
  /** Round-3 image prices (#3089 R5), flattened to one row per priced image. */
  imagePrices: FairUseImagePriceRow[]
  /** Round-3 voice token prices (#3089 R6), one row per voice model. */
  voiceTokenPrices: FairUseVoiceTokenPriceRow[]
  /** Round-2 lead approval allowance (R11). Delegable, so it lives on Limits. */
  leadAllowanceSek: string
}

export function fairUseConfigToFormState(config: FairUseConfig): FairUseConfigFormState {
  return {
    enabled: config.enabled,
    defaultMonthlyCapSek: String(config.default_monthly_cap_sek),
    proMonthlyCapSek: String(config.pro_monthly_cap_sek),
    unlimitedMonthlyCapSek: String(config.unlimited_monthly_cap_sek),
    warnPercent: String(config.warn_percent),
    requestCtaPercent: String(config.request_cta_percent),
    downgradePercent: String(config.downgrade_percent),
    blockPercent: String(config.block_percent),
    downgradeModel: config.downgrade_model,
    downgradeReasoningEffort: normalizeFairUseReasoningEffort(config.downgrade_reasoning_effort),
    blockCapSek: String(config.block_cap_sek),
    blockListPriceSek: String(config.block_list_price_sek),
    overrides: { ...config.overrides },
    entitlements: fairUseEntitlementsToFormState(config.entitlements),
    voicePrices: fairUseVoicePricesToFormState(config.voice_price_sek_per_minute),
    imagePrices: fairUseImagePricesToFormState(config.image_price_sek),
    voiceTokenPrices: fairUseVoiceTokenPricesToFormState(config.voice_token_prices),
    // Absent on a round-1 backend. Rendered as an empty box rather than "0",
    // so the panel never states an allowance the platform has not set — see
    // `fairUseHasLeadAllowance`, which decides whether the field is shown at all.
    leadAllowanceSek:
      typeof config.lead_allowance_sek_per_month === "number"
        ? String(config.lead_allowance_sek_per_month)
        : "",
  }
}

/** A plain decimal literal, once the separator is normalized — no exponent, no 0x, no "Infinity". */
const DECIMAL_LITERAL = /^-?\d+(\.\d+)?$/

/**
 * A single separator with exactly three digits behind it and at most three in
 * front: "1,000", "1,995", "250.000". Grouped thousands to an English typist,
 * a three-decimal number to a Swedish one — see {@link parseDecimalField}.
 * Four or more leading digits rule the grouping reading out ("1234,567" would
 * have to be written "1,234,567"), so only 1–3 are ambiguous.
 */
const AMBIGUOUS_GROUPING = /^-?\d{1,3}[.,]\d{3}$/

/**
 * A numeric form field, comma or point as the decimal separator.
 *
 * The rule, in full — these fields set what x-one invoices, so anything that
 * could be read two ways is refused rather than guessed at:
 *
 * 1. Leading/trailing whitespace is trimmed (`String.trim` covers the
 *    no-break spaces a paste from a formatted number carries, U+00A0/U+202F).
 * 2. "," and "." are BOTH decimal separators, and exactly one may appear.
 *    "1,50" and "1.50" are 1.5; "1.234,50" and "1,234.50" carry two and are
 *    rejected. A Swedish admin's decimal comma is the whole point: bare
 *    `Number("1,50")` is NaN, which made {@link fairUseFormStateToConfig}
 *    return null and killed the Save button with no explanation.
 * 3. NO internal whitespace is accepted — not even as thousands grouping.
 *    Stripping it turned "1 2 3" into 123 and "10 0" into 100.
 * 4. A single separator followed by EXACTLY three digits is ambiguous between
 *    a decimal and grouped thousands, so it is rejected: "1,000" as a decimal
 *    is 1, a 1000x under-read of a 1000 SEK price, and no evidence in the
 *    string says which the admin meant. Write "1000", "1,00" or "1,0050".
 * 5. The regex is what keeps `Number`'s silent coercions out: "0x10" (16),
 *    "1e400" (Infinity), "Infinity", "12abc" and blank input are all rejected.
 */
function parseDecimalField(value: string): number | null {
  const trimmed = value.trim()
  if (AMBIGUOUS_GROUPING.test(trimmed)) return null
  // Only the FIRST separator is normalized: a second one then fails the
  // literal test instead of collapsing into a valid-looking number.
  const normalized = trimmed.replace(",", ".")
  if (!DECIMAL_LITERAL.test(normalized)) return null
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Parse a numeric form field the server requires to be strictly positive.
 *
 * Mirrors `FairUseAdmin._validate_config_updates`, which rejects `<= 0` for
 * every numeric config field except `block_list_price_sek` — accepting 0 here
 * would only produce a 400 at save time.
 */
export function parsePositiveNumber(value: string): number | null {
  const parsed = parseDecimalField(value)
  return parsed != null && parsed > 0 ? parsed : null
}

/** Parse the one numeric field the server allows to be 0 (`block_list_price_sek`). */
export function parseNonNegativeNumber(value: string): number | null {
  const parsed = parseDecimalField(value)
  return parsed != null && parsed >= 0 ? parsed : null
}

/**
 * Parse the form back into a full config payload, or null when any numeric
 * field is invalid or the downgrade model is empty (save must stay disabled).
 */
export function fairUseFormStateToConfig(form: FairUseConfigFormState): FairUseConfig | null {
  const defaultMonthlyCapSek = parsePositiveNumber(form.defaultMonthlyCapSek)
  const proMonthlyCapSek = parsePositiveNumber(form.proMonthlyCapSek)
  const unlimitedMonthlyCapSek = parsePositiveNumber(form.unlimitedMonthlyCapSek)
  const warnPercent = parsePositiveNumber(form.warnPercent)
  const requestCtaPercent = parsePositiveNumber(form.requestCtaPercent)
  const downgradePercent = parsePositiveNumber(form.downgradePercent)
  const blockPercent = parsePositiveNumber(form.blockPercent)
  const blockCapSek = parsePositiveNumber(form.blockCapSek)
  // The only field the backend lets be 0 — a free/bundled block.
  const blockListPriceSek = parseNonNegativeNumber(form.blockListPriceSek)
  const downgradeModel = form.downgradeModel.trim()

  if (
    defaultMonthlyCapSek == null ||
    proMonthlyCapSek == null ||
    unlimitedMonthlyCapSek == null ||
    warnPercent == null ||
    requestCtaPercent == null ||
    downgradePercent == null ||
    blockPercent == null ||
    blockCapSek == null ||
    blockListPriceSek == null ||
    !downgradeModel
  ) {
    return null
  }

  return {
    enabled: form.enabled,
    default_monthly_cap_sek: defaultMonthlyCapSek,
    pro_monthly_cap_sek: proMonthlyCapSek,
    unlimited_monthly_cap_sek: unlimitedMonthlyCapSek,
    warn_percent: warnPercent,
    request_cta_percent: requestCtaPercent,
    downgrade_percent: downgradePercent,
    block_percent: blockPercent,
    downgrade_model: downgradeModel,
    // The select can only hold a valid stop, so there is nothing to parse or
    // reject here — it is normalized on the way IN (`fairUseConfigToFormState`).
    downgrade_reasoning_effort: form.downgradeReasoningEffort,
    block_cap_sek: blockCapSek,
    block_list_price_sek: blockListPriceSek,
    overrides: form.overrides,
  }
}

/**
 * Which side of the write split EVERY config field falls on — `true` for the
 * commercial envelope the backend accepts from PLATFORM admins only
 * (`PLATFORM_ONLY_CONFIG_FIELDS` in helloai_utilities/fair_use/admin.py),
 * `false` for the fields a delegated `fair_use` admin may write.
 *
 * The commercial ones are the inputs the invoice is computed from — the block
 * price is stamped onto every grant and summed into `grant_revenue_sek`, and
 * the caps decide whether a billing event is produced at all. Sending any of
 * them without platform-admin rights answers 403 `platform_admin_required`,
 * and nothing is applied (not even the legal half of the payload).
 *
 * `Record<keyof FairUseConfig, boolean>` is the load-bearing part: adding a
 * field to {@link FairUseConfig} without classifying it here is a COMPILE
 * error. A hand-written delegable list could silently omit the new field —
 * `FairUseConfigUpdate` is a Partial, so nothing would complain — and a
 * delegated admin editing it would get a successful save that changed
 * nothing, while the backend considered the field delegable all along.
 */
const FAIR_USE_CONFIG_FIELD_IS_PLATFORM_ONLY: Record<keyof FairUseConfig, boolean> = {
  enabled: false,
  default_monthly_cap_sek: true,
  pro_monthly_cap_sek: true,
  unlimited_monthly_cap_sek: true,
  warn_percent: false,
  request_cta_percent: false,
  downgrade_percent: false,
  block_percent: false,
  downgrade_model: false,
  // Enforcement tuning, not a commercial term: it only decides how cheaply an
  // over-cap turn is served. The worst a delegated admin can do with it is make
  // their own users' downgraded answers more expensive — the opposite of a
  // revenue bypass — so the backend keeps it off PLATFORM_ONLY_CONFIG_FIELDS.
  downgrade_reasoning_effort: false,
  block_cap_sek: true,
  block_list_price_sek: true,
  overrides: true,
  // Round 2. The matrix decides what every plan may DO — Deep, voice, flow and
  // image caps, the per-turn budgets — so a delegated admin owning it could
  // move their whole tenant onto the Unlimited feature row without touching a
  // single SEK figure. The voice rate is an outright invoice input.
  entitlements: true,
  voice_price_sek_per_minute: true,
  // Round 3. Both are outright invoice inputs: one decides what an image
  // costs, the other what a realtime voice token costs.
  image_price_sek: true,
  voice_token_prices: true,
  // Delegable, like the thresholds: it only decides how much of the tenant's
  // OWN budget a lead may hand out instead of the admin, and every krona of it
  // is already billed through the same grant path with the same list price.
  lead_allowance_sek_per_month: false,
}

const FAIR_USE_CONFIG_FIELDS = Object.keys(
  FAIR_USE_CONFIG_FIELD_IS_PLATFORM_ONLY
) as (keyof FairUseConfig)[]

/** The commercial fields — read-only in the panel, and never sent, unless platform admin. */
export const FAIR_USE_PLATFORM_ONLY_CONFIG_FIELDS: readonly (keyof FairUseConfig)[] =
  FAIR_USE_CONFIG_FIELDS.filter((field) => FAIR_USE_CONFIG_FIELD_IS_PLATFORM_ONLY[field])

/**
 * What a delegated admin may PUT: enforcement, the four thresholds, the
 * downgrade model and the reasoning effort that goes with it.
 */
export const FAIR_USE_DELEGABLE_CONFIG_FIELDS: readonly (keyof FairUseConfig)[] =
  FAIR_USE_CONFIG_FIELDS.filter((field) => !FAIR_USE_CONFIG_FIELD_IS_PLATFORM_ONLY[field])

/** Narrow assignment helper — keeps the copy below key-by-key type-checked. */
function copyConfigField<K extends keyof FairUseConfig>(
  target: FairUseConfigUpdate,
  config: FairUseConfig,
  field: K
): void {
  target[field] = config[field]
}

/**
 * The subset of a parsed config a delegated (non-platform) admin may PUT.
 *
 * The panel keeps the commercial inputs read-only for such an admin, but it
 * must also stop SENDING them — an unchanged `block_list_price_sek` in
 * `updates` is still a rejected field to the backend, so a delegated admin
 * could never save a threshold change at all.
 */
export function fairUseDelegableConfigUpdate(config: FairUseConfig): FairUseConfigUpdate {
  const updates: FairUseConfigUpdate = {}
  for (const field of FAIR_USE_DELEGABLE_CONFIG_FIELDS) {
    copyConfigField(updates, config, field)
  }
  return updates
}

/**
 * Config fields the panel's Save never sends, whoever is saving.
 *
 * `overrides` is the whole per-user plan map. The config PUT applies the
 * fields it is given onto the LATEST stored document, so sending the map means
 * sending this browser's snapshot of it — and a plan another admin (or another
 * worker's `set_user_tier`) assigned after this form was loaded is inside that
 * snapshot's blind spot and would be dropped. Per-user assignment has its own
 * endpoint, `PUT /api/admin/fair-use/users/<oid>/tier`, which merges ONE oid
 * against the latest document under an If-Match; that is the only write path
 * for plans, from the usage table and from the overrides editor alike.
 */
const FAIR_USE_NEVER_SENT_CONFIG_FIELDS: readonly (keyof FairUseConfig)[] = ["overrides"]

/**
 * What the platform-admin Save puts on the wire: every config field except the
 * ones {@link FAIR_USE_NEVER_SENT_CONFIG_FIELDS} keeps off it.
 */
export function fairUsePlatformConfigUpdate(config: FairUseConfig): FairUseConfigUpdate {
  const updates: FairUseConfigUpdate = {}
  for (const field of FAIR_USE_CONFIG_FIELDS) {
    if (FAIR_USE_NEVER_SENT_CONFIG_FIELDS.includes(field)) continue
    copyConfigField(updates, config, field)
  }
  return updates
}

/**
 * True when the per-user overrides came back carrying the caps that were sent.
 *
 * Only the user ids and their `cap_sek` are compared, because the server owns
 * the rest of an override record: `_validate_overrides` stamps `set_by` and
 * `ts` on the way in and normalizes a missing `reason` to `""`. A structural
 * equality would therefore report a discard on every single save.
 */
function fairUseOverridesApplied(
  sent: Record<string, FairUseOverride>,
  saved: Record<string, FairUseOverride> | undefined
): boolean {
  // A Map rather than index access on the Record: a key present in `sent`
  // need not be present in the server's answer, and `Map.get` is the lookup
  // whose type admits that.
  const savedById = new Map(Object.entries(saved ?? {}))
  const sentIds = Object.keys(sent)
  if (sentIds.length !== savedById.size) return false
  return sentIds.every((id) => {
    const savedOverride = savedById.get(id)
    if (!savedOverride) return false
    const sentOverride = sent[id]
    return (
      (sentOverride.tier ?? null) === (savedOverride.tier ?? null) &&
      (sentOverride.cap_sek ?? null) === (savedOverride.cap_sek ?? null)
    )
  })
}

/**
 * The fields of `updates` the platform did NOT apply, read off the config the
 * PUT itself answered with.
 *
 * A 200 is not the same statement as "the value is now the configuration".
 * `FairUseAdmin.update_config` merges `updates` field by field
 * (`_validate_config_updates` builds a NEW dict and only copies the keys it has
 * a branch for), so a field can be dropped inside a success response — and on a
 * surface that decides what x-one invoices, a discarded value shown as saved is
 * the worst possible failure mode. The route answers with the post-write config
 * precisely so the client can check rather than assume.
 *
 * An answer with no config at all counts as "nothing confirmed": every sent
 * field is reported, because a save that cannot be verified must not be
 * presented as one that was.
 */
export function fairUseUnappliedConfigFields(
  updates: FairUseConfigUpdate,
  saved: FairUseConfig | undefined
): (keyof FairUseConfig)[] {
  const sentFields = Object.keys(updates) as (keyof FairUseConfig)[]
  if (!saved) return sentFields
  return sentFields.filter((field) => {
    const sent = updates[field]
    if (sent === undefined) return false
    if (field === "overrides") {
      return !fairUseOverridesApplied(sent as Record<string, FairUseOverride>, saved.overrides)
    }
    // The five nested fields are compared STRUCTURALLY. Everything else on
    // this config is a scalar, so `!==` is the whole story there — but two
    // objects off two round trips are never the same reference, and an
    // identity check would report every entitlement or voice-rate save as
    // discarded and drown the real "the platform dropped a field" signal.
    if (field === "entitlements") {
      return !fairUseEntitlementsApplied(sent as FairUseEntitlements, saved.entitlements)
    }
    if (field === "voice_price_sek_per_minute") {
      return !fairUseVoicePricesApplied(
        sent as FairUseVoicePrices,
        saved.voice_price_sek_per_minute
      )
    }
    if (field === "image_price_sek") {
      return !fairUseImagePricesApplied(sent as FairUseImagePrices, saved.image_price_sek)
    }
    if (field === "voice_token_prices") {
      return !fairUseVoiceTokenPricesApplied(
        sent as FairUseVoiceTokenPrices,
        saved.voice_token_prices
      )
    }
    return saved[field] !== sent
  })
}

/** The graduated policy only makes sense when warn ≤ cta ≤ downgrade ≤ block. */
export function areFairUseThresholdsOrdered(config: FairUseConfig): boolean {
  return (
    config.warn_percent <= config.request_cta_percent &&
    config.request_cta_percent <= config.downgrade_percent &&
    config.downgrade_percent <= config.block_percent
  )
}

/** Plan amounts must stay Standard ≤ Pro ≤ Unlimited. */
export function areFairUseTierCapsOrdered(config: FairUseConfig): boolean {
  return (
    config.default_monthly_cap_sek <= config.pro_monthly_cap_sek &&
    config.pro_monthly_cap_sek <= config.unlimited_monthly_cap_sek
  )
}

export const FAIR_USE_ASSIGNABLE_TIERS: readonly FairUseTier[] = ["standard", "pro", "unlimited"]

export function resolveFairUsePlan(override?: FairUseOverride | null): FairUsePlan {
  if (!override) return "standard"
  if (override.tier === "standard" || override.tier === "pro" || override.tier === "unlimited") {
    return override.tier
  }
  if (override.cap_sek != null && override.cap_sek > 0) return "custom"
  return "standard"
}

export function fairUsePlanCapSek(
  plan: FairUsePlan,
  config: Pick<
    FairUseConfig,
    "default_monthly_cap_sek" | "pro_monthly_cap_sek" | "unlimited_monthly_cap_sek"
  >,
  override?: FairUseOverride
): number | undefined {
  if (override?.cap_sek != null && override.cap_sek > 0) return override.cap_sek
  switch (plan) {
    case "standard":
      return config.default_monthly_cap_sek
    case "pro":
      return config.pro_monthly_cap_sek
    case "unlimited":
      return config.unlimited_monthly_cap_sek
    case "custom":
      return override?.cap_sek
    default: {
      const _exhaustive: never = plan
      return _exhaustive
    }
  }
}

/**
 * The selectable downgrade deployments for a config response, defensively.
 *
 * Accepts the list at the response root (where the platform derives it from
 * `CONFIG_CHAT_MODELS` — it is not part of the config document or its value
 * hash) and, tolerantly, nested inside `config`, so the panel does not break
 * if the field lands on the other side of that line. Entries without a usable
 * string `id` are dropped and a missing/blank `label` falls back to the id:
 * the id is what gets written, so an unlabeled deployment is still a valid,
 * pickable choice — it just shows its raw name.
 *
 * Returns `[]` for an older backend that sends no list at all. The field's
 * ABSENCE and an EMPTY list must be told apart by the caller, which is why
 * this returns the array and `hasFairUseDowngradeModelOptions` answers the
 * other question separately.
 */
export function readFairUseDowngradeModelOptions(
  response: FairUseConfigResponse | undefined
): FairUseDowngradeModelOption[] {
  const raw =
    response?.downgrade_model_options ??
    (response?.config as { downgrade_model_options?: unknown } | undefined)?.downgrade_model_options
  if (!Array.isArray(raw)) return []
  const options: FairUseDowngradeModelOption[] = []
  const seen = new Set<string>()
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue
    const { id, label, selected } = entry as { id?: unknown; label?: unknown; selected?: unknown }
    if (typeof id !== "string" || !id || seen.has(id)) continue
    seen.add(id)
    options.push({
      id,
      label: typeof label === "string" && label ? label : id,
      ...(selected === true ? { selected: true } : {}),
    })
  }
  return options
}

/**
 * Whether the SAVED `downgrade_model` is one the platform can actually resolve.
 *
 * Not an id comparison, deliberately. The platform resolves the configured
 * model suffix-tolerantly (`model_catalog.py`: a config saying
 * `gpt-5.6-terra` matches a `gpt-5.6-terra_eu` deployment) and reports the
 * outcome as `selected` on the option it landed on — which is the ONLY
 * trustworthy answer, since the very bug this whole option list exists to
 * prevent was a suffix mismatch that made downgrades silently do nothing.
 * Comparing ids here instead would flag a perfectly working legacy config as
 * "not deployed" — a false alarm on the one screen that is supposed to tell
 * the admin the truth about this setting.
 *
 * Only meaningful for the STORED value: `selected` describes what the server
 * last read, so a caller must stop trusting it the moment the admin edits the
 * field (see `FairUseDowngradeFields.isModelResolvedByPlatform`).
 */
export function isFairUseDowngradeModelResolved(
  response: FairUseConfigResponse | undefined
): boolean {
  return readFairUseDowngradeModelOptions(response).some((option) => option.selected)
}
